import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import VendorList from '../components/VendorList'
import { API_BASE } from '../config'

export default function CaseDetail() {
    const { id } = useParams()
    const navigate = useNavigate()
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [activeTab, setActiveTab] = useState('brief')
    const [email, setEmail] = useState('')
    const [checklist, setChecklist] = useState('')
    const [generating, setGenerating] = useState(false)
    const [teamNote, setTeamNote] = useState('')
    const [savingNote, setSavingNote] = useState(false)

    const loadData = useCallback(async () => {
        try {
            setLoading(true)
            const res = await fetch(`${API_BASE}/cases/${id}`)
            if (!res.ok) throw new Error('Failed to load case')
            const caseJson = await res.json()

            // Also fetch the full brief with debate_brief + stored_brief
            const briefRes = await fetch(`${API_BASE}/cases/${id}/brief`)
            let briefData = null
            if (briefRes.ok) {
                briefData = await briefRes.json()
            }

            // Combine: use case data from first call, full brief from second
            setData(briefData || caseJson)
            setTeamNote(caseJson.case?.team_note || '')
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }, [id])

    useEffect(() => { loadData() }, [loadData])

    async function generateEmail() {
        setGenerating(true)
        try {
            const res = await fetch(`${API_BASE}/outputs/${id}/email`, { method: 'POST' })
            if (!res.ok) throw new Error('Failed to generate email')
            const json = await res.json()
            setEmail(json.email)
            setActiveTab('email')
        } catch (err) {
            setError(err.message)
        } finally {
            setGenerating(false)
        }
    }

    async function generateChecklist() {
        setGenerating(true)
        try {
            const res = await fetch(`${API_BASE}/outputs/${id}/checklist`, { method: 'POST' })
            if (!res.ok) throw new Error('Failed to generate checklist')
            const json = await res.json()
            setChecklist(json.checklist)
            setActiveTab('checklist')
        } catch (err) {
            setError(err.message)
        } finally {
            setGenerating(false)
        }
    }

    async function saveNote() {
        setSavingNote(true)
        try {
            const res = await fetch(`${API_BASE}/cases/${id}/note`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ note: teamNote }),
            })
            if (!res.ok) throw new Error('Failed to save note')
        } catch (err) {
            setError(err.message)
        } finally {
            setSavingNote(false)
        }
    }

    function copyToClipboard(text) {
        navigator.clipboard.writeText(text)
        alert('Copied to clipboard!')
    }

    if (loading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
                <p>Loading case...</p>
            </div>
        )
    }

    if (error) {
        return (
            <div className="card">
                <p style={{ color: 'var(--error)' }}>Error: {error}</p>
                <button className="btn btn-primary mt-2" onClick={loadData}>
                    Retry
                </button>
            </div>
        )
    }

    if (!data || !data.case) {
        return <div className="card"><p>Case not found</p></div>
    }

    const { case: c, stored_brief, debate_brief, team_note: savedNote } = data
    // Reconstruct 'brief' to match what BriefContent expects
    const brief = {
        case: c,
        stored_brief,
        debate_brief,
        // Pass through any other legacy fields that might exist
        solutions: data.solutions,
        recommended_solution: data.recommended_solution,
        alternative_solutions: data.alternative_solutions,
        vendor_inquiry_points: data.vendor_inquiry_points,
        follow_up_questions: data.follow_up_questions,
        sources: data.sources,
    }
    const isResearching = c.status === 'researching'

    return (
        <div>
            {/* Header */}
            <div className="flex-between mb-3">
                <div>
                    <h2>{c.case_number} — {c.client_name}</h2>
                    <p className="text-muted">{c.problem_description}</p>
                </div>
                <div className="flex gap-2">
                    <button className="btn btn-secondary" onClick={generateEmail} disabled={generating || isResearching}>
                        {generating && activeTab !== 'email' ? 'Generating...' : 'Generate Email'}
                    </button>
                    <button className="btn btn-secondary" onClick={generateChecklist} disabled={generating || isResearching}>
                        {generating && activeTab !== 'checklist' ? 'Generating...' : 'Generate Checklist'}
                    </button>
                    <button className="btn btn-primary" onClick={() => navigate('/')}>
                        Back
                    </button>
                </div>
            </div>

            {isResearching && (
                <div className="card mb-3" style={{ borderColor: 'var(--warning)' }}>
                    <div className="loading" style={{ padding: '1rem' }}>
                        <div className="spinner"></div>
                        <p>Research in progress... This may take 30-60 seconds.</p>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="tabs">
                <button
                    className={`tab ${activeTab === 'brief' ? 'active' : ''}`}
                    onClick={() => setActiveTab('brief')}
                >
                    Brief
                </button>
                <button
                    className={`tab ${activeTab === 'email' ? 'active' : ''}`}
                    onClick={() => setActiveTab('email')}
                >
                    Email Draft
                </button>
                <button
                    className={`tab ${activeTab === 'checklist' ? 'active' : ''}`}
                    onClick={() => setActiveTab('checklist')}
                >
                    Checklist
                </button>
                <button
                    className={`tab ${activeTab === 'sources' ? 'active' : ''}`}
                    onClick={() => setActiveTab('sources')}
                >
                    Sources
                </button>
                <button
                    className={`tab ${activeTab === 'notes' ? 'active' : ''}`}
                    onClick={() => setActiveTab('notes')}
                >
                    Team Notes
                </button>
            </div>

            {/* Tab Content */}
            <div className="tab-content">
                {activeTab === 'brief' && (
                    brief ? (
                        <BriefContent brief={brief} onCopy={copyToClipboard} />
                    ) : (
                        <p className="text-muted">No brief available yet.</p>
                    )
                )}

                {activeTab === 'email' && (
                    email ? (
                        <div className="card">
                            <div className="flex-between mb-2">
                                <h3>Email Draft</h3>
                                <button className="copy-btn" onClick={() => copyToClipboard(email)}>
                                    Copy
                                </button>
                            </div>
                            <pre className="pre-content">{email}</pre>
                        </div>
                    ) : (
                        <p className="text-muted">Click "Generate Email" to create a vendor inquiry email.</p>
                    )
                )}

                {activeTab === 'checklist' && (
                    checklist ? (
                        <div className="card">
                            <div className="flex-between mb-2">
                                <h3>Vendor Checklist</h3>
                                <button className="copy-btn" onClick={() => copyToClipboard(checklist)}>
                                    Copy
                                </button>
                            </div>
                            <pre className="pre-content">{checklist}</pre>
                        </div>
                    ) : (
                        <p className="text-muted">Click "Generate Checklist" to create vendor questions.</p>
                    )
                )}

                {activeTab === 'sources' && brief?.sources && (
                    <div className="card">
                        <h3>Research Sources</h3>
                        {brief.sources.map((src, i) => (
                            <div key={i} className="mb-2">
                                <a href={src.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold)' }}>
                                    {src.title}
                                </a>
                                <p className="text-sm text-muted">{src.snippet?.substring(0, 150)}...</p>
                            </div>
                        ))}
                    </div>
                )}

                {activeTab === 'notes' && (
                    <div className="card team-notes">
                        <h3>Team Notes</h3>
                        <textarea
                            value={teamNote}
                            onChange={(e) => setTeamNote(e.target.value)}
                            placeholder="Add notes for the team..."
                        />
                        <button
                            className="btn btn-primary mt-2"
                            onClick={saveNote}
                            disabled={savingNote}
                        >
                            {savingNote ? 'Saving...' : 'Save Note'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}

// Extract solutions from brief data — prefer debate_brief.proposals
const extractSolutions = (briefData) => {
    if (!briefData) return []

    // Priority 1: debate_brief proposals (nested format)
    if (briefData.debate_brief?.proposals?.length > 0) {
        return briefData.debate_brief.proposals
    }

    // Priority 2: stored_brief solutions (flat products)
    if (briefData.stored_brief?.solutions?.length > 0) {
        return [{
            rank: 1,
            title: 'Recommended Solution',
            description: briefData.stored_brief.problem_summary || '',
            products: briefData.stored_brief.solutions,
            total_estimated_cost: 'See individual products'
        }]
    }

    // Priority 3: legacy top-level solutions
    if (briefData.solutions && briefData.solutions.length > 0) {
        return briefData.solutions
    }

    // Priority 4: recommended_solution + alternatives
    const result = []
    if (briefData.recommended_solution) {
        result.push({ rank: 1, ...briefData.recommended_solution })
    }
    if (briefData.alternative_solutions) {
        briefData.alternative_solutions.forEach((s, i) => {
            result.push({ rank: i + 2, ...s })
        })
    }
    return result
}

const extractSummary = (briefData) => {
    return briefData?.stored_brief?.problem_summary ||
        briefData?.debate_brief?.content?.summary ||
        briefData?.problem_summary ||
        ''
}

const extractScore = (briefData) => {
    return briefData?.case?.final_score ||
        briefData?.debate_brief?.score || 0
}

const extractRounds = (briefData) => {
    return briefData?.case?.total_rounds ||
        briefData?.debate_brief?.rounds || 0
}

const RANK_EMOJIS = ['🥇', '🥈', '🥉', '4️⃣', '5️⃣']

function BriefContent({ brief, onCopy }) {
    // brief is the full API response: { case, stored_brief, debate_brief, ... }
    const solutions = extractSolutions(brief)
    const summary = extractSummary(brief)
    const score = extractScore(brief)
    const rounds = extractRounds(brief)
    const caseInfo = brief?.case || {}
    const debateBrief = brief?.debate_brief || {}
    const storedBrief = brief?.stored_brief || {}

    // Log debug info for development
    console.log('BriefContent debug:', {
        debateProposals: debateBrief?.proposals?.length || 0,
        storedSolutions: storedBrief?.solutions?.length || 0,
        topSolutions: brief?.solutions?.length || 0,
        hasRecommended: !!brief?.recommended_solution
    })

    return (
        <div>
            {/* Header card */}
            <div style={{
                background: 'rgba(212,175,55,0.1)',
                border: '1px solid #D4AF37',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                }}>
                    <span style={{ color: '#D4AF37', fontWeight: 'bold' }}>
                        {caseInfo.status === 'researching' ? '🔍 Researching...' : '✅ Research Complete'}
                    </span>
                    {score > 0 && (
                        <span style={{
                            background: score >= 60 ? '#10B981' : '#EF4444',
                            color: 'white',
                            padding: '2px 10px',
                            borderRadius: '12px',
                            fontSize: '13px'
                        }}>
                            {score}% • {rounds} rounds
                        </span>
                    )}
                </div>
                {summary && (
                    <p style={{ color: '#ccd6f6', margin: 0, fontSize: '14px', lineHeight: 1.5 }}>
                        {summary}
                    </p>
                )}
                <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                    <button className="copy-btn" onClick={() => {
                        const text = JSON.stringify(brief, null, 2)
                        onCopy(text)
                    }}>
                        Copy All
                    </button>
                </div>
            </div>

            {/* Solutions */}
            {solutions.length === 0 ? (
                <div style={{
                    color: '#8899aa', padding: '20px', textAlign: 'center',
                    background: '#1e2a3a', borderRadius: '8px'
                }}>
                    No solutions available yet.
                    {caseInfo.status === 'researching' && ' Research is in progress...'}
                </div>
            ) : (
                solutions.map((solution, idx) => (
                    <SolutionCard
                        key={idx}
                        solution={solution}
                        rank={solution.rank || idx + 1}
                    />
                ))
            )}

            {/* Questions for client */}
            {brief?.debate_brief?.proposals?.[0]?.questions_for_client && (
                <div style={{
                    marginTop: '20px',
                    background: '#142238',
                    borderRadius: '8px',
                    padding: '16px'
                }}>
                    <h3 style={{ color: '#D4AF37', fontSize: '15px', marginBottom: '12px' }}>
                        ❓ Questions for Client
                    </h3>
                    {brief.debate_brief.proposals[0].questions_for_client.map((q, i) => (
                        <p key={i} style={{ color: '#94a3b8', margin: '4px 0', fontSize: '13px' }}>
                            • {q}
                        </p>
                    ))}
                </div>
            )}

            {/* Vietnamese questions */}
            {brief?.debate_brief?.proposals?.[0]?.questions_for_client_vi && (
                <div style={{
                    marginTop: '12px',
                    background: '#142238',
                    borderRadius: '8px',
                    padding: '16px'
                }}>
                    <h3 style={{ color: '#D4AF37', fontSize: '15px', marginBottom: '12px' }}>
                        ❓ Câu hỏi cho khách hàng
                    </h3>
                    {brief.debate_brief.proposals[0].questions_for_client_vi.map((q, i) => (
                        <p key={i} style={{ color: '#94a3b8', margin: '4px 0', fontSize: '13px' }}>
                            • {q}
                        </p>
                    ))}
                </div>
            )}

            {/* Legacy vendor inquiry points */}
            {brief?.vendor_inquiry_points && brief.vendor_inquiry_points.length > 0 && (
                <div style={{
                    marginTop: '12px',
                    background: '#142238',
                    borderRadius: '8px',
                    padding: '16px'
                }}>
                    <h3 style={{ color: '#D4AF37', fontSize: '15px', marginBottom: '12px' }}>
                        📋 Vendor Inquiry Points
                    </h3>
                    {brief.vendor_inquiry_points.map((q, i) => (
                        <p key={i} style={{ color: '#94a3b8', margin: '4px 0', fontSize: '13px' }}>
                            • {q}
                        </p>
                    ))}
                </div>
            )}

            {/* Legacy follow-up questions */}
            {brief?.follow_up_questions && brief.follow_up_questions.length > 0 && (
                <div style={{
                    marginTop: '12px',
                    background: '#142238',
                    borderRadius: '8px',
                    padding: '16px'
                }}>
                    <h3 style={{ color: '#D4AF37', fontSize: '15px', marginBottom: '12px' }}>
                        ❓ Clarify With Client
                    </h3>
                    {brief.follow_up_questions.map((q, i) => (
                        <p key={i} style={{ color: '#94a3b8', margin: '4px 0', fontSize: '13px' }}>
                            • {q}
                        </p>
                    ))}
                </div>
            )}

            {/* Sources */}
            {brief?.sources && brief.sources.length > 0 && (
                <div style={{
                    marginTop: '20px',
                    background: '#142238',
                    borderRadius: '8px',
                    padding: '16px'
                }}>
                    <h3 style={{ color: '#D4AF37', fontSize: '15px', marginBottom: '12px' }}>
                        📚 Research Sources
                    </h3>
                    {brief.sources.map((src, i) => (
                        <div key={i} style={{ marginBottom: '8px' }}>
                            <a href={src.url} target="_blank" rel="noopener noreferrer"
                                style={{ color: '#D4AF37', fontSize: '14px' }}>
                                {src.title || src.url}
                            </a>
                            {src.snippet && (
                                <p style={{ color: '#64748b', fontSize: '12px', margin: '2px 0 0' }}>
                                    {src.snippet.substring(0, 150)}...
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

function SolutionCard({ solution, rank }) {
    const [expanded, setExpanded] = useState(rank === 1)
    const products = solution.products || []
    const strengths = solution.strengths || solution.key_strengths || solution.pros || []
    const weaknesses = solution.weaknesses || solution.known_limitations || solution.cons || []
    const covers = solution.covers_requirements || []
    const notes = solution.implementation_notes || ''
    const description = solution.description || solution.why_suitable || ''
    const cost = solution.total_estimated_cost || ''
    const title = solution.title || solution.name || `Solution ${rank}`
    const isPrimary = rank === 1

    return (
        <div style={{
            border: isPrimary
                ? '1px solid #D4AF37'
                : '1px solid #1e3a5f',
            borderRadius: '8px',
            marginBottom: '16px',
            overflow: 'hidden',
            background: '#142238'
        }}>
            {/* Solution header - always visible */}
            <div
                onClick={() => setExpanded(!expanded)}
                style={{
                    padding: '16px',
                    cursor: 'pointer',
                    background: isPrimary
                        ? 'rgba(212,175,55,0.05)'
                        : 'transparent',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                }}
            >
                <div style={{ flex: 1 }}>
                    <div style={{
                        marginBottom: '4px'
                    }}>
                        <span style={{ fontSize: '18px' }}>{RANK_EMOJIS[rank - 1] || `${rank}`}</span>
                        <span style={{
                            fontWeight: 'bold',
                            color: isPrimary ? '#D4AF37' : '#ccd6f6',
                            fontSize: '15px',
                            marginLeft: '6px'
                        }}>
                            {title}
                        </span>
                    </div>
                    {cost && (
                        <div style={{ color: '#10B981', fontSize: '13px', marginTop: '4px' }}>
                            💰 {cost}
                        </div>
                    )}
                </div>
                <div style={{
                    color: '#D4AF37',
                    fontSize: '20px',
                    marginLeft: '12px',
                    flexShrink: 0
                }}>
                    {expanded ? '▲' : '▼'}
                </div>
            </div>

            {/* Expanded content */}
            {expanded && (
                <div style={{ padding: '0 16px 16px' }}>
                    {description && (
                        <p style={{
                            color: '#8899aa',
                            fontSize: '13px',
                            margin: '0 0 12px',
                            lineHeight: 1.5
                        }}>
                            {description}
                            {solution.description_vi && (
                                <span style={{ display: 'block', marginTop: '4px', color: '#64748b' }}>
                                    ({solution.description_vi})
                                </span>
                            )}
                        </p>
                    )}

                    {covers.length > 0 && (
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{
                                color: '#D4AF37',
                                fontSize: '12px',
                                marginBottom: '4px',
                                fontWeight: 'bold'
                            }}>
                                ✅ COVERS:
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                {covers.map((c, i) => (
                                    <span key={i} style={{
                                        background: 'rgba(16,185,129,0.1)',
                                        color: '#10B981',
                                        padding: '2px 8px',
                                        borderRadius: '10px',
                                        fontSize: '11px'
                                    }}>{c}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Products */}
                    {products.length > 0 && (
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{
                                color: '#D4AF37',
                                fontSize: '12px',
                                marginBottom: '8px',
                                fontWeight: 'bold'
                            }}>
                                📦 PRODUCTS & VENDORS:
                            </div>
                            {products.map((p, i) => (
                                <ProductCard key={i} product={p} />
                            ))}
                        </div>
                    )}

                    {/* Strengths & weaknesses */}
                    {(strengths.length > 0 || weaknesses.length > 0) && (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '12px',
                            marginBottom: '8px'
                        }}>
                            {strengths.length > 0 && (
                                <div>
                                    <div style={{
                                        color: '#10B981',
                                        fontSize: '11px',
                                        marginBottom: '4px',
                                        fontWeight: 'bold'
                                    }}>
                                        ✅ STRENGTHS
                                    </div>
                                    {strengths.map((s, i) => (
                                        <div key={i} style={{
                                            color: '#8899aa',
                                            fontSize: '11px',
                                            marginBottom: '2px'
                                        }}>• {s}</div>
                                    ))}
                                </div>
                            )}
                            {weaknesses.length > 0 && (
                                <div>
                                    <div style={{
                                        color: '#EF4444',
                                        fontSize: '11px',
                                        marginBottom: '4px',
                                        fontWeight: 'bold'
                                    }}>
                                        ⚠️ LIMITATIONS
                                    </div>
                                    {weaknesses.map((w, i) => (
                                        <div key={i} style={{
                                            color: '#8899aa',
                                            fontSize: '11px',
                                            marginBottom: '2px'
                                        }}>• {w}</div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {notes && (
                        <div style={{
                            background: 'rgba(255,255,255,0.03)',
                            borderRadius: '6px',
                            padding: '10px',
                            fontSize: '12px',
                            color: '#8899aa',
                            lineHeight: 1.5
                        }}>
                            📋 {notes}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

function ProductCard({ product }) {
    return (
        <div style={{
            background: 'var(--bg-tertiary)',
            borderRadius: '6px',
            padding: '0.75rem',
            marginBottom: '0.5rem',
            border: '1px solid var(--border-color)',
            borderLeft: product.from_portfolio ? '3px solid var(--gold-600)' : 'none'
        }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                {/* Star badge for portfolio products */}
                {product.from_portfolio && (
                    <span style={{ fontSize: '1rem', flexShrink: 0 }} title="LEXIS Portfolio">&#11088;</span>
                )}
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.9rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                        {product.name}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
                        &#127970; {product.vendor}
                        {product.vendor_country && ` (${product.vendor_country})`}
                    </div>
                    {product.role_in_solution && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                            &#127919; {product.role_in_solution}
                        </div>
                    )}
                    <div style={{ fontSize: '0.8rem', fontWeight: '600', color: 'var(--green-400)', marginTop: '0.25rem' }}>
                        &#128176; Est. {product.estimated_cost_usd}
                    </div>
                    {product.availability_vietnam && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                            &#127483;&#127475; Vietnam: {product.availability_vietnam}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
